import { TranslateService } from '@ngx-translate/core';
import { EventEmitter, Injectable, Output } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { Response } from 'src/app/models/response.model';
import { User } from 'src/app/models/user.model';
import { HttpHelpersService } from '../http-helpers/http-helpers.service';
import { Device } from '@awesome-cordova-plugins/device/ngx';
import { config } from '../../config'
import { UtilsService } from '../utils/utils.service';
import { MenuController, Platform } from '@ionic/angular';
import { NotificationsService } from '../notifications/notifications.service';
import { EditUserRequest } from 'src/app/models/editUserRequest.model';
import { ApiSelectorService } from '../api-selector/api-selector.service';
import { PushNotifications } from '@capacitor/push-notifications';
import { MenuService } from '../menu/menu.service';

@Injectable({
  providedIn: 'root'
})
export class LoginService {

  private url: string;
  private subject = new Subject<any>();
  @Output() checkingTokenRefresh = new EventEmitter<any>();
  @Output() tokenRefreshed = new EventEmitter<any>();

  constructor(
    private http: HttpClient,
    private httpHelpers: HttpHelpersService,
    private device: Device,
    private utils: UtilsService,
    private menuCtrl: MenuController,
    private platform: Platform,
    private notificationService: NotificationsService,
    private apiSelectorService: ApiSelectorService,
    private translate : TranslateService,
    private menuService: MenuService
  ) {
  }

  getCompanyLogo(company): Observable<any> {
    let httpOptions = {
      headers: {
        'accept-language': this.translate.currentLang
      }
    }
    return this.http.get<Response>(`${this.apiSelectorService.SERVER_CONFIG}/empresa/${company}`, httpOptions)
      .pipe(catchError(this.httpHelpers.errorHandler("")));
  }

  authenticateUser(userInfo, company?): Observable<Response> {
    let httpOptions = {};
    if (company) {
      httpOptions = {
        ...this.httpHelpers.setParams(
          {
            'empresa': company,
          }
        )
      }
    }
    httpOptions = {
      ...httpOptions, headers: {
        'accept-language': this.translate.currentLang
      }
    }
    this.url = this.httpHelpers.getUrl();
    return this.http.post<Response>(`${this.url}/auth/login`, userInfo, httpOptions).pipe(catchError(this.httpHelpers.errorHandler(this.translate.instant('login-service.autenticar usuario'))));
  }

  getUserInfo(token?): Observable<any> {
    if (token)
      this.httpHelpers.setHeader(token.access_token);


    const httpOptions = {
      ...this.httpHelpers.getHeader()
    }
    this.url = this.httpHelpers.getUrl();
    return this.http.get<Response>(`${this.url}/auth/usuario`, httpOptions).pipe(map((data) => {
      const expiration_date = token ? new Date(new Date().getTime() + (token.expires_in * 1000)) : null;
      const { uuid, model, platform, version } = this.device;
      const device = {
        uuid,
        model,
        os: `${platform} ${version}`,
      }

      data.data = { ...data.data, ...token, expiration_date, ...device };

      return data
    }), catchError(this.httpHelpers.errorHandler(this.translate.instant('login-service.obtener datos de usuario'))));
  }

  updateUser(user: User) {
    this.subject.next(user);
  }

  getUser(): Observable<User> {
    return this.subject.asObservable();
  }

  refreshToken(token): Observable<any> {
    this.url = this.httpHelpers.getUrl();
    let httpOptions = {
      headers: {
        'accept-language': this.translate.currentLang
      }
    }
    return this.http.post<Response>(`${this.url}/auth/login`, token, httpOptions).pipe(map((data: any) => {
      this.httpHelpers.setHeader(data.access_token);
      const expiration_date = new Date(new Date().getTime() + (data.expires_in * 1000));
      data.data = { ...data, expiration_date }
      return data;
    }),
      catchError(this.httpHelpers.errorHandler(this.translate.instant('login-service.actualizar credenciales'))));
  }

  changePassword(password): Observable<any> {
    const httpOptions = {
      ...this.httpHelpers.getHeader()
    }
    this.url = this.httpHelpers.getUrl();
    return this.http.put<Response>(`${this.url}/auth/usuario/password`, password, httpOptions).pipe(map((data: any) => {
      return data.data;
    }),
      catchError(this.httpHelpers.errorHandler(this.translate.instant('login-service.cambiar contraseña'))));

  }

  recoverPassword(email) {
    this.url = this.httpHelpers.getUrl();
    let httpOptions = {
      headers: {
        'accept-language': this.translate.currentLang
      }
    }
    return this.http.post<Response>(`${this.url}/auth/usuario/password/reset`, email, httpOptions).pipe(map((data: any) => {
      return data.data;
    }),
      catchError(this.httpHelpers.errorHandler(this.translate.instant('login-service.recuperar contraseña'))));
  }

  editUser(user): Observable<any> {
    const httpOptions = {
      ...this.httpHelpers.getHeader()
    }
    this.url = this.httpHelpers.getUrl();
    return this.http.put<Response>(`${this.url}/auth/usuario`, user, httpOptions).pipe(map((data: any) => {
      return data;
    }),
      catchError(this.httpHelpers.errorHandler(this.translate.instant('login-service.editar usuario'))));
  }

  getUserConfig(company): Observable<any> {
    const httpOptions = {
      ...this.httpHelpers.getHeader()
    }
    this.url = this.httpHelpers.getUrl();
    return this.http.get<Response>(`${this.url}/empresas/${company}/configuracion`, httpOptions).pipe(map((data: any) => {
      return data;
    }),
      catchError(this.httpHelpers.errorHandler(this.translate.instant('login-service.obtener configuración'))));
  }

  getUserConfigPromises(company): Promise<any> {
    const httpOptions = {
      ...this.httpHelpers.getHeader()
    }
    this.url = this.httpHelpers.getUrl();
    return this.http.get<Response>(`${this.url}/empresas/${company}/configuracion`, httpOptions).pipe(map((data: any) => {
      return data;
    }),
      catchError(this.httpHelpers.errorHandler(this.translate.instant('login-service.obtener configuración')))).toPromise();
  }

  logout(uuid?): Observable<any> {
    PushNotifications.removeAllDeliveredNotifications();
    const httpOptions = {
      ...this.httpHelpers.getHeader()
    }
    this.url = this.httpHelpers.getUrl();
    return this.http.post<Response>(`${this.url}/auth/logout`, uuid, httpOptions).pipe(map(({ data }) => {
      return data
    }), catchError(this.httpHelpers.errorHandler(this.translate.instant('login-service.cerrar sesión'))));
  }



  sendReport(report): Observable<any> {
    const httpOptions = {
      ...this.httpHelpers.getHeader()
    }
    this.url = this.httpHelpers.getUrl();
    return this.http.post<Response>(`${this.url}/reportar`, report, httpOptions).pipe(map((data: any) => {
      return data;
    }),
      catchError(this.httpHelpers.errorHandler(this.translate.instant('login-service.enviar comentario'))));
  }


  async loginUser(userCredentials, company?): Promise<any> {
    return new Promise(async (res, rej) => {

      this.authenticateUser(userCredentials, company).
        subscribe(
          (data) => {
            if (data.error) {
              if (data.error.status === 401)
                this.utils.showMessage(data.operation);
              else
                this.utils.showMessage(data.operation);
              this.utils.dismissLoading();
              rej();
            } else {
              this.getUserInfo(data).subscribe(async ({ data: user, operation, error }: { data: User, operation, error }) => {
                if (error) {
                  this.utils.showMessage(operation);
                  rej();
                }
                else {
                  this.utils.saveInStorage('passwordForFingerprintCredentials', this.utils.encryptData(userCredentials.get('password')));
                  user.initials = this.utils.getUserInitials(user.nombre, user.apellido);
                  this.updateUser(user);
                  try {
                    if (this.platform.is('capacitor')) {
                      if (this.platform.is('ios') || this.platform.is('android')) {
                        const { token } = await this.notificationService.initializeNotifications(user);
                        user.token = token;
                        user.notifications = true;
                        this.utils.saveInStorage('user', user);
                      } else {
                        user.notifications = false;
                        this.utils.saveInStorage('user', user);
                      }
                    } else {
                      user.notifications = false;
                      this.utils.saveInStorage('user', user);
                    }
                  } catch (e) {
                    user.notifications = false;
                    this.utils.saveInStorage('user', user);
                  }
                  this.utils.saveInStorage('showNotificationsMsg', true);
                  this.utils.saveInStorage('isEmailNotificationActive', user.notificacion_email);
                  if (user.fecha_verificacion)
                    this.menuService.menuTranslate();
                    this.menuCtrl.enable(true);
                  this.utils.dismissLoading();
                  res({ user });
                }
              })
            }
          }
        );
    })
  }

  async checkTokenValidity(): Promise<any> {
    return new Promise(async (res, rej) => {
      let user = await this.utils.getFromStorage('user');
      const expiration = new Date(user.expiration_date);
      const tokenRenewalDate = new Date(expiration.setDate(expiration.getDate() - 30)).getTime();
      const today = new Date().getTime();

      if (tokenRenewalDate - today < 0) {
        const fd = new FormData();
        fd.append('refresh_token', user.refresh_token);
        let password = await this.utils.getFromStorage('passwordForFingerprintCredentials');
        password = this.utils.decryptData(password);
        this.refreshToken(fd)
          .subscribe(async ({ data, error, operation }) => {
            if (error) {
              this.authenticateUser({ username: user.email, password }).subscribe(async (authenticateUserData) => {
                if (authenticateUserData.error) {
                  this.utils.showMessage(operation);
                  rej(400);
                }
                else {
                  user = { ...user, ...authenticateUserData };
                  user.expiration_date = new Date(new Date().getTime() + (authenticateUserData['expires_in'] * 1000));
                  await this.utils.saveInStorage('user', user);
                  this.tokenRefreshed.emit();
                  res(200);
                }
              })
            } else {

              for (const key in data) {
                if (user[key]) {
                  user[key] = data[key];
                }
              }
              await this.utils.saveInStorage('user', user);
              res(200);
            }
          })
      } else {
        res(200);
      }
    })
  }

  /**
   * Llamada al servicio para editar los datos del usuario, dni, email, telefono
   * @param editUserRequest Objeto con los parametros a editar (dni, email, telefono) 
   * @returns Objeto de tipo usuario con la informacion del usuario
   */
  editUserInfo(editUserRequest: EditUserRequest): Observable<any> {
    const httpOptions = {
      ...this.httpHelpers.getHeader()
    }
    this.url = this.httpHelpers.getUrl();
    return this.http.put<Response>(`${this.url}/auth/usuario`, editUserRequest, httpOptions).pipe(map((data: any) => {
      return data.data;
    }),
      catchError(this.httpHelpers.errorHandler(this.translate.instant('login-service.Actualizar información del usuario'))));
  }

}
